import { type JSX, splitProps } from 'solid-js'

export type BadgeVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral'
  | 'info'
  | 'outline'
export type BadgeSize = 'sm' | 'md'

export interface BadgeProps extends JSX.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  size?: BadgeSize
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'border-transparent bg-[var(--color-neutral-200)] text-[color:var(--color-neutral-700)]',
  primary: 'border-transparent bg-[var(--color-primary-100)] text-[color:var(--color-primary-700)]',
  secondary:
    'border-transparent bg-[var(--color-secondary-100)] text-[color:var(--color-secondary-700)]',
  success: 'border-transparent bg-[var(--color-success-100)] text-[color:var(--color-success-700)]',
  warning: 'border-transparent bg-[var(--color-warning-100)] text-[color:var(--color-warning-800)]',
  error: 'border-transparent bg-[var(--color-error-100)] text-[color:var(--color-error-700)]',
  neutral:
    'border-[var(--color-neutral-400)] bg-[var(--color-neutral-200)] text-[color:var(--color-neutral-700)]',
  info: 'border-transparent bg-[var(--color-info-100)] text-[color:var(--color-info-700)]',
  outline:
    'border-[var(--color-neutral-300)] bg-[var(--color-surface)] text-[color:var(--color-neutral-700)]',
}

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'min-h-6 px-2.5 py-0.5 text-xs leading-4',
  md: 'min-h-7 px-3 py-0.5 text-sm leading-5',
}

export function Badge(props: BadgeProps) {
  const [local, rest] = splitProps(props, ['variant', 'size', 'class', 'children'])

  const variant = () => local.variant ?? 'default'
  const size = () => local.size ?? 'md'

  return (
    <span
      {...rest}
      class={`
        ds-badge inline-flex items-center justify-center gap-1.5 whitespace-nowrap tabular-nums border
        font-[var(--font-medium)]
        rounded-[var(--radius-capsule)]
        ${variantClasses[variant()]}
        ${sizeClasses[size()]}
        ${local.class ?? ''}
      `.trim()}
    >
      {local.children}
    </span>
  )
}
